<script setup>
  import { ref, computed, onMounted, defineProps, inject, watch } from 'vue'
  // import { storeToRefs } from 'pinia'
  import { useFaqsStore } from '../stores/FaqsStore'
  import TextWithDefinitions from './TextWithDefinitions.vue'

  const faqStore = useFaqsStore()
  const customNavigation = inject('customNavigation')
  // const { getFaqs } = storeToRefs(faqStore)
  const { getFaqs } = faqStore
  const localFaqs = ref([])
  const faqColumns = ref([[], []])
  const faqTitles = ref([])
  const isLoaded = ref(false)

  const props = defineProps({
    /**
     * One section id, or several. With more than one the preview splits its slots evenly
     * between them — a user with Content Library and Transform sees two questions of each.
     */
    section: {
      type: [String, Array],
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    /**
     * Appends ": {sectionName}" to the card title. Off for the limited-access Home, where
     * the persona already implies the module and a second section would make it ambiguous.
     */
    showSectionName: {
      type: Boolean,
      default: true
    }
  })

  /*
   * A section with no authored questions renders an empty state instead of the two columns,
   * and hides the link to the full page (there would be nothing there either). That is the
   * current situation for Transform: the FAQS node in Realtime Database has no `transform`
   * section yet, so the card stays empty until the content is authored — no code change
   * needed once it exists.
   */
  const hasQuestions = computed(() => localFaqs.value.length > 0)

  /** Title suffix only makes sense when a single section is on show and the caller wants it. */
  const sectionLabel = computed(() =>
    props.showSectionName && faqTitles.value.length === 1 ? faqTitles.value[0] : ''
  )

  watch(
    () => props.loading,
    (newValue) => {
      if (!newValue) {
        isLoaded.value = true
      }
    }
  )

  /** Questions shown in the Home preview; the rest live on the full FAQs page. */
  const PREVIEW_LIMIT = 4

  onMounted(async () => {
    const remoteFaqs = await getFaqs()
    const faqsArray = Object.values(remoteFaqs.value ?? {})
    const wanted = Array.isArray(props.section) ? props.section : [props.section]
    const matches = wanted
      .map((id) => faqsArray.find((val) => val.sectionId === id))
      .filter(Boolean)

    isLoaded.value = true
    if (matches.length === 0) {
      return
    }

    // Split the preview evenly: one section fills all four slots, two take two each.
    const perSection = Math.max(1, Math.floor(PREVIEW_LIMIT / matches.length))
    const bySection = matches.map((match) =>
      JSON.parse(match.FaqList ?? '[]').slice(0, perSection)
    )

    localFaqs.value = bySection.flat().slice(0, PREVIEW_LIMIT)
    faqTitles.value = matches.map((match) => match.sectionName)

    /*
     * Columns are built here rather than filtered by index parity in the template, because
     * the two cases want different groupings:
     *   - two sections → one module per column, so a reader never has to guess which module
     *     a question belongs to;
     *   - one section  → the original parity split, reading across the two columns.
     */
    faqColumns.value =
      bySection.length > 1
        ? [bySection[0], bySection[1]]
        : [
            localFaqs.value.filter((_, i) => i % 2 === 0),
            localFaqs.value.filter((_, i) => i % 2 === 1)
          ]
  })
</script>

<template>
  <div class="row d-flex justify-content-center mt-4.5 px-1">
    <div class="col-md-12 pe-2">
      <div class="card">
        <div class="card-header bg-light px-4 pt-2.5">
          <h5 id="faqTitle" class="card-title add-teamates-link header-20 text-secondary">
            <!-- The colon only appears when there is a section name to follow it. -->
            <strong>Frequently Asked Questions{{ sectionLabel ? ':' : '' }}</strong>
            {{ sectionLabel }}
          </h5>
          <a
            v-if="hasQuestions"
            class="view-full-profile-link hand-cursor text-decoration-none text-14 text-primary"
            @click.prevent="
              customNavigation.navigate({
                newPage: customNavigation.routes.frequentlyQuestions.name
              })
            "
            href="#">
            <strong id="goToAllFAQsHome">Go to All Frequently Asked Questions</strong>
            <i class="bi bi-chevron-right ms-2"></i>
          </a>
        </div>
        <div class="card-body ps-4">
          <div v-if="isLoaded && !hasQuestions" id="faqsEmptyState" class="text-center text-14 text-secondary py-4">
            Frequently asked questions will appear here.
          </div>
          <div v-else-if="hasQuestions" class="row">
            <!-- Left column  -->
            <div class="col-md-6 faq-col-left">
              <div v-for="(faq, index) in faqColumns[0]" :key="index" class="faq-item">
                <h5 class="text-14 text-secondary mb-2">
                  <strong>{{ faq.faqTitle }}</strong>
                </h5>
                <p class="text-14 text-secondary mb-0">
                  <TextWithDefinitions :key="faq.faqBody" :text="faq.faqBody" />
                </p>
              </div>
            </div>
            <!-- Right column  -->
            <div class="col-md-6 faq-col-right">
              <div v-for="(faq, index) in faqColumns[1]" :key="index" class="faq-item">
                <h5 class="text-14 text-secondary mb-2">
                  <strong>{{ faq.faqTitle }}</strong>
                </h5>
                <p class="text-14 text-secondary mb-0">
                  <TextWithDefinitions :key="faq.faqBody" :text="faq.faqBody" />
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .view-full-profile-link {
    position: relative;
    float: right;
    margin-top: 5px;
  }

  .add-teamates-link {
    display: contents;
  }

  /*
   * 20px between one question and the next. Applied between siblings rather than as a
   * bottom margin, so the last question in a column does not add trailing space.
   */
  .faq-item + .faq-item {
    margin-top: 20px;
  }

  /*
   * 41px between the two columns of questions. The gap is owned entirely by the left
   * column's padding, so the number reads as one value instead of a sum of two.
   */
  .faq-col-left {
    padding-right: 41px;
  }

  .faq-col-right {
    padding-left: 0;
    padding-right: 48px;
  }
</style>
